import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { cssMap } from '@compiled/react';
import { defineMessages, type IntlShape } from 'react-intl';

import { aiMessages } from '@atlaskit/editor-common/ai-messages/ai';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics/types/enums';
import type { Command, CommandDispatch } from '@atlaskit/editor-common/types/command';
import type {
	FloatingToolbarButton,
	FloatingToolbarCustom,
} from '@atlaskit/editor-common/types/floating-toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { MarkType, NodeType } from '@atlaskit/editor-prosemirror/model';
import { TextSelection, type EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { useSmartLinkContext } from '@atlaskit/link-provider/use-smart-link-context';
import type { SmartLinkResponse } from '@atlaskit/linking-types/smart-link';
import { RovoIcon } from '@atlaskit/logo/rovo/icon';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { Box } from '@atlaskit/primitives/compiled/box';
import { expValNoExposure } from '@atlaskit/tmp-editor-statsig/expVal';
import { token } from '@atlaskit/tokens';
import { SMART_LINK_ROVO_BUTTON_INTERACTION_SOURCE } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import sharedMessages from '@atlassian/generative-ai-modal/utils/messages';

import type { AIPlugin } from '../../../aiPluginType';
import { createOpenAIModalCommand } from '../../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn } from '../../../types/types';
import { SummarizeIcon } from '../../icons/prompt/summarize';
import type { EditorPluginAIConfigItem } from '../../prebuilt/config-items/config-items';
import { summarizeSmartlink } from '../../prebuilt/config-items/prompts/summarize-smartlink/summarize-smartlink';
import {
	getSmartLinkRovoMetadata,
	type SmartLinkRovoMetadata,
} from '../../smartlink-rovo/smartlink-metadata';
import { useResolvedSmartLinkForRovo } from '../../smartlink-rovo/use-resolved-smartlink';
import { createSmartLinkSummarizeCommand } from '../../smartlink-summarize/create-smartlink-summarize-command';
import { SmartLinkToolbarControls } from './floating-toolbar-button';
import { getSmartLinkDropdownItems } from './smartlink-action-items';
import { useSmartLinkAIActions, type SmartLinkAIAction } from './use-smartlink-ai-actions';

export const floatingToolbarAIButtonTestID = 'ak-editor-ai-selection-toolbar-ai-button';
const SMART_LINK_ROVO_BUTTON_INVOKED_FROM = SMART_LINK_ROVO_BUTTON_INTERACTION_SOURCE;
const SMART_LINK_SUMMARIZE_BUTTON_INVOKED_FROM = ACTION_SUBJECT_ID.SMART_LINK_SUMMARIZE_BUTTON;

const rovoButtonIconStyles = cssMap({
	root: { paddingInlineStart: token('space.075'), paddingInlineEnd: token('space.025') },
	smallerRovoIcon: { display: 'inline-flex', transform: 'scale(0.75)', transformOrigin: 'center' },
});

const isPasteActionsMenuV2Enabled = () =>
	['hasAltAiActions', 'hasSpellingAndGrammar'].includes(
		expValNoExposure('platform_editor_paste_actions_menu_v2', 'variant', 'control'),
	);

export const getSmartLinkRovoButton = ({
	api,
	aiGlobalOptIn,
	disabled,
	has3pSources,
	intl,
	nodeType,
	rangeBaseGenerate,
	smartlinkName,
	smartlinkUrl,
	sourceProduct,
	triggeredForMark,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	disabled: boolean;
	has3pSources?: boolean;
	intl: IntlShape;
	nodeType: NodeType;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	smartlinkName: string;
	smartlinkUrl: string;
	sourceProduct?: string | null;
	triggeredForMark?: MarkType;
}): FloatingToolbarButton<Command> => {
	const RovoButtonIcon = () => (
		<Box as="span" xcss={rovoButtonIconStyles.root}>
			<Box as="span" xcss={rovoButtonIconStyles.smallerRovoIcon}>
				<RovoIcon size="xxsmall" />
			</Box>
		</Box>
	);

	return {
		type: 'button',
		isRadioButton: false,
		disabled,
		showTitle: true,
		testId: `${floatingToolbarAIButtonTestID}-smartlink`,
		icon: RovoButtonIcon,
		title: intl.formatMessage(sharedMessages.rovoButtonTitle),
		ariaLabel: intl.formatMessage(sharedMessages.rovoButtonTitle),
		tooltipContent: intl.formatMessage(sharedMessages.rovoButtonTitle),
		onMount: () => {},
		onClick: (state: EditorState, dispatch?: CommandDispatch, _view?: EditorView) => {
			api?.analytics?.actions.fireAnalyticsEvent({
				action: ACTION.CLICKED,
				actionSubject: ACTION_SUBJECT.BUTTON,
				eventType: EVENT_TYPE.UI,
				actionSubjectId: ACTION_SUBJECT_ID.SMART_LINK_ROVO_BUTTON,
				attributes: {
					sourceProduct,
					has3pSources,
				},
			});

			// Open the same prompt input as getAIExperienceButton(), but with
			// openInRovoChat: true so that submitting the prompt opens the Rovo
			// chat sidebar instead of performing an AI edit on the document.
			// rovoChatAIFeatureContext carries the smartlink entity context so that
			// the Rovo sidebar shows a context banner for the linked resource.
			const openAIModalCommand = createOpenAIModalCommand({
				state,
				configItem: {
					...rangeBaseGenerate,
					openInRovoChat: true,
					rovoChatInteractionSource: SMART_LINK_ROVO_BUTTON_INVOKED_FROM,
					rovoChatAIFeatureContext: smartlinkName
						? {
								projectContext: {
									projectId: smartlinkUrl,
									projectName: smartlinkName,
									projectUrl: smartlinkUrl,
								},
							}
						: undefined,
				},
				lastTriggeredFrom: SMART_LINK_ROVO_BUTTON_INVOKED_FROM,
				triggerMethod: 'click',
				aiGlobalOptIn,
				triggeredForMark,
				triggeredFor: nodeType,
				locale: intl.locale,
			});
			openAIModalCommand(state, dispatch);
			return false;
		},
	};
};

export const getSmartLinkSummarizeButton = ({
	api,
	disabled,
	intl,
	aiGlobalOptIn,
	nodeType,
	nodeRange,
	smartlinkUrl,
	sourceProduct,
	has3pSources,
	triggeredForMark,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	disabled: boolean;
	has3pSources?: boolean;
	intl: IntlShape;
	/**
	 * Document range of the smartlink the toolbar was opened for. Preferred over
	 * `state.selection`, which is the *current* selection — and may not match the
	 * smartlink (e.g. when the toolbar is shown via hover/focus rather than a
	 * click that selects the node).
	 */
	nodeRange?: { from: number; to: number };
	nodeType: NodeType;
	smartlinkUrl: string;
	sourceProduct?: string | null;
	triggeredForMark?: MarkType;
}): FloatingToolbarButton<Command> => {
	return {
		type: 'button',
		isRadioButton: false,
		disabled,
		showTitle: true,
		testId: `${floatingToolbarAIButtonTestID}-smartlink-summarize`,
		icon: SummarizeIcon,
		title: intl.formatMessage(aiMessages.summarizeToolbarDropdownIconTitle),
		ariaLabel: intl.formatMessage(aiMessages.summarizeToolbarDropdownIconTitle),
		tooltipContent: intl.formatMessage(aiMessages.summarizeToolbarDropdownIconTitle),
		onMount: () => {},
		onClick: (state: EditorState, dispatch?: CommandDispatch, _view?: EditorView) => {
			if (isPasteActionsMenuV2Enabled()) {
				return createSmartLinkSummarizeCommand({
					aiGlobalOptIn,
					decorateDeselectTransaction: api?.card?.actions.hideLinkToolbar,
					fireAnalyticsEvent: api?.analytics?.actions.fireAnalyticsEvent,
					has3pSources,
					lastTriggeredFrom: SMART_LINK_SUMMARIZE_BUTTON_INVOKED_FROM,
					locale: intl.locale,
					nodeRange,
					nodeType,
					openSmartLinkSummarizePopup: api?.aiDefinitions?.actions.openSmartLinkSummarizePopup,
					smartlinkUrl,
					sourceProduct,
					triggeredForMark,
					triggerMethod: 'click',
				})(state, dispatch, _view);
			}

			api?.analytics?.actions.fireAnalyticsEvent({
				action: ACTION.CLICKED,
				actionSubject: ACTION_SUBJECT.BUTTON,
				eventType: EVENT_TYPE.UI,
				actionSubjectId: ACTION_SUBJECT_ID.SMART_LINK_SUMMARIZE_BUTTON,
				attributes: {
					sourceProduct,
					has3pSources,
				},
			});

			if (api?.aiDefinitions?.actions.openSmartLinkSummarizePopup) {
				// Prefer the smartlink's own range (passed in from the descriptor
				// builder, where it's already known) over `state.selection` —
				// the toolbar can be shown via hover/focus without the selection
				// covering the node, in which case `state.selection` would point
				// at unrelated content.
				const { from, to } = nodeRange ?? state.selection;
				// Deselect the smartlink node before opening the popup so the
				// smartlink floating toolbar closes immediately. Clamp the target
				// position to the document end — `to + 1` would overshoot when a
				// blockCard sits at the very end of the doc.
				if (dispatch) {
					const deselectAt = Math.min(to + 1, state.doc.content.size);
					let tr = state.tr.setSelection(TextSelection.near(state.doc.resolve(deselectAt), 1));
					if (api.card?.actions.hideLinkToolbar) {
						tr = api.card.actions.hideLinkToolbar(tr);
					}
					dispatch(tr);
				}
				api.aiDefinitions.actions.openSmartLinkSummarizePopup({
					from,
					to,
					smartlinkUrl,
					sourceProduct,
					has3pSources,
					invokedFrom: SMART_LINK_SUMMARIZE_BUTTON_INVOKED_FROM,
				});
				return false;
			} else {
				const openAIModalCommand = createOpenAIModalCommand({
					state,
					configItem: summarizeSmartlink({ smartlinkUrl }),
					lastTriggeredFrom: SMART_LINK_SUMMARIZE_BUTTON_INVOKED_FROM,
					triggerMethod: 'click',
					aiGlobalOptIn,
					locale: intl.locale,
					triggeredFor: nodeType,
					sourceProduct,
					has3pSources,
					triggeredForMark,
				});
				openAIModalCommand(state, dispatch);
			}
			return false;
		},
	};
};

const getSmartLinkButtonDescriptors = ({
	api,
	aiGlobalOptIn,
	disabled,
	intl,
	nodeRange,
	nodeType,
	rangeBaseGenerate,
	smartlinkUrl,
	triggeredForMark,
	metadata,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	disabled: boolean;
	intl: IntlShape;
	metadata: SmartLinkRovoMetadata;
	nodeRange?: { from: number; to: number };
	nodeType: NodeType;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	smartlinkUrl: string;
	triggeredForMark?: MarkType;
}) => ({
	rovoDescriptor: getSmartLinkRovoButton({
		api,
		aiGlobalOptIn,
		disabled,
		has3pSources: metadata.has3pSources,
		intl,
		nodeType,
		rangeBaseGenerate,
		smartlinkName: metadata.smartlinkName,
		smartlinkUrl,
		sourceProduct: metadata.sourceProduct,
		triggeredForMark,
	}),
	summarizeDescriptor: getSmartLinkSummarizeButton({
		api,
		aiGlobalOptIn,
		disabled,
		intl,
		nodeType,
		nodeRange,
		smartlinkUrl,
		sourceProduct: metadata.sourceProduct,
		has3pSources: metadata.has3pSources,
		triggeredForMark,
	}),
});

const LegacySmartLinkAIButtonsRendererBaseline = ({
	api,
	aiGlobalOptIn,
	areAnyNewToolbarFlagsEnabled,
	disabled,
	intl,
	isHyperlink,
	nodeType,
	nodeRange,
	rangeBaseGenerate,
	smartlinkUrl,
	view,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	areAnyNewToolbarFlagsEnabled: boolean;
	disabled: boolean;
	intl: IntlShape;
	isHyperlink?: boolean;
	nodeRange?: { from: number; to: number };
	nodeType: NodeType;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	smartlinkUrl: string;
	view: EditorView;
}): React.JSX.Element | null => {
	const { store, connections } = useSmartLinkContext();
	const [hyperlinkResolved, setHyperlinkResolved] = useState(false);

	// For plain hyperlinks, the URL is not yet in the SmartLink store (only card
	// components trigger resolution automatically). We trigger resolution on mount
	// so we can check access permissions before showing AI buttons.
	useEffect(() => {
		if (!isHyperlink) {
			return;
		}
		const existing = store?.getState()[smartlinkUrl];
		if (existing) {
			setHyperlinkResolved(true);
			return;
		}
		connections.client
			.fetchData(smartlinkUrl)
			.then((response) => {
				store?.dispatch({
					type: 'resolved',
					url: smartlinkUrl,
					payload: response,
				});
				setHyperlinkResolved(true);
			})
			.catch(() => {
				// If resolution fails, buttons won't render — correct behavior
				// for inaccessible URLs.
			});
	}, [isHyperlink, smartlinkUrl, store, connections.client]);

	const smartLinkState = store?.getState()[smartlinkUrl];
	const { status, details } = smartLinkState || {};
	const metadata = getSmartLinkRovoMetadata({ details, smartlinkUrl });

	const isResolved = status === 'resolved' && details?.meta?.access === 'granted';

	// For hyperlinks, wait for resolution to complete before deciding
	if (isHyperlink && !hyperlinkResolved) {
		return null;
	}

	if (!isResolved) {
		return null;
	}

	const triggeredForMark = isHyperlink ? view.state.schema.marks.link : undefined;
	const isSmartlinkToolbarV2ExpEnabled = isExperimentEnabled(
		'platform_editor_ai_smartlink_toolbar_v2_exp',
	);
	const dropdownItems = isSmartlinkToolbarV2ExpEnabled
		? getSmartLinkDropdownItems({
				aiGlobalOptIn,
				details,
				intl,
				nodeType,
				smartlinkUrl,
				testIdPrefix: floatingToolbarAIButtonTestID,
				triggeredForMark,
				view,
			})
		: [];

	const { rovoDescriptor, summarizeDescriptor } = getSmartLinkButtonDescriptors({
		api,
		aiGlobalOptIn,
		disabled,
		intl,
		nodeType,
		nodeRange,
		rangeBaseGenerate,
		smartlinkUrl,
		triggeredForMark,
		metadata,
	});

	return (
		<SmartLinkToolbarControls
			areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
			disabled={disabled}
			dropdownItems={dropdownItems}
			intl={intl}
			rovoDescriptor={rovoDescriptor}
			summarizeDescriptor={summarizeDescriptor}
			view={view}
		/>
	);
};

const RefactoredSmartLinkAIButtonsRendererBaseline = ({
	api,
	aiGlobalOptIn,
	areAnyNewToolbarFlagsEnabled,
	disabled,
	intl,
	isHyperlink,
	nodeType,
	nodeRange,
	rangeBaseGenerate,
	smartlinkUrl,
	view,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	areAnyNewToolbarFlagsEnabled: boolean;
	disabled: boolean;
	intl: IntlShape;
	isHyperlink?: boolean;
	nodeRange?: { from: number; to: number };
	nodeType: NodeType;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	smartlinkUrl: string;
	view: EditorView;
}): React.JSX.Element | null => {
	const { details, isLoading, isResolvedWithAccess, metadata } = useResolvedSmartLinkForRovo({
		resolveIfMissing: Boolean(isHyperlink),
		smartlinkUrl,
	});

	// For hyperlinks, wait for resolution to complete before deciding
	if (isHyperlink && isLoading) {
		return null;
	}

	if (!isResolvedWithAccess || !metadata) {
		return null;
	}

	const triggeredForMark = isHyperlink ? view.state.schema.marks.link : undefined;
	const isSmartlinkToolbarV2ExpEnabled = isExperimentEnabled(
		'platform_editor_ai_smartlink_toolbar_v2_exp',
	);
	const dropdownItems = isSmartlinkToolbarV2ExpEnabled
		? getSmartLinkDropdownItems({
				aiGlobalOptIn,
				details,
				intl,
				nodeType,
				smartlinkUrl,
				testIdPrefix: floatingToolbarAIButtonTestID,
				triggeredForMark,
				view,
			})
		: [];
	// Re-use the existing descriptor-based helpers to keep all button config in
	// one place, then pass each field to the FloatingToolbarButton React component
	// the same way the Toolbar's own `case 'button':` branch does it.

	const { rovoDescriptor, summarizeDescriptor } = getSmartLinkButtonDescriptors({
		api,
		aiGlobalOptIn,
		disabled,
		intl,
		nodeType,
		nodeRange,
		rangeBaseGenerate,
		smartlinkUrl,
		triggeredForMark,
		metadata,
	});

	return (
		<SmartLinkToolbarControls
			areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
			disabled={disabled}
			dropdownItems={dropdownItems}
			intl={intl}
			rovoDescriptor={rovoDescriptor}
			summarizeDescriptor={summarizeDescriptor}
			view={view}
		/>
	);
};

const smartLinkMessages = defineMessages({
	retry: {
		id: 'fabric.editor.ai.smartlink.retry',
		defaultMessage: 'Retry loading AI actions',
		description: 'Retries Smart Link metadata loading after a request failed.',
	},
	noActions: {
		id: 'fabric.editor.ai.smartlink.no-actions',
		defaultMessage: 'No additional AI actions are available for this link',
		description: 'Shown when a resolved Smart Link provider has no additional AI actions.',
	},
	loading: {
		id: 'fabric.editor.ai.smartlink.loading',
		defaultMessage: 'Loading AI actions…',
		description: 'Shown while AI toolbar actions wait for Smart Link metadata.',
	},
});

type SmartLinkAIButtonsRendererProps = {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	areAnyNewToolbarFlagsEnabled: boolean;
	disabled: boolean;
	intl: IntlShape;
	isHyperlink?: boolean;
	nodeRange?: { from: number; to: number };
	nodeType: NodeType;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	smartlinkUrl: string;
	view: EditorView;
};

const StableSmartLinkAIButtonsRenderer = ({
	api,
	aiGlobalOptIn,
	areAnyNewToolbarFlagsEnabled,
	disabled,
	intl,
	isHyperlink,
	nodeType,
	nodeRange,
	rangeBaseGenerate,
	smartlinkUrl,
	view,
}: SmartLinkAIButtonsRendererProps): React.JSX.Element | null => {
	const triggeredForMark = isHyperlink ? view.state.schema.marks.link : undefined;
	const getActionHandler = useCallback(
		(action: SmartLinkAIAction, details: SmartLinkResponse, itemTestId?: string) => {
			if (action === 'dropdown') {
				return getSmartLinkDropdownItems({
					aiGlobalOptIn,
					details,
					intl,
					nodeType,
					smartlinkUrl,
					testIdPrefix: floatingToolbarAIButtonTestID,
					triggeredForMark,
					view,
				}).find((item) => item.testId === itemTestId)?.onClick;
			}
			const metadata = getSmartLinkRovoMetadata({ details, smartlinkUrl });
			const descriptors = getSmartLinkButtonDescriptors({
				api,
				aiGlobalOptIn,
				disabled,
				intl,
				nodeType,
				nodeRange,
				rangeBaseGenerate,
				smartlinkUrl,
				triggeredForMark,
				metadata,
			});
			const descriptor =
				action === 'rovo' ? descriptors.rovoDescriptor : descriptors.summarizeDescriptor;
			return () => descriptor.onClick(view.state, view.dispatch, view);
		},
		[
			api,
			aiGlobalOptIn,
			disabled,
			intl,
			nodeRange,
			nodeType,
			rangeBaseGenerate,
			smartlinkUrl,
			triggeredForMark,
			view,
		],
	);
	const {
		details,
		dispatchAction,
		failed,
		hidden,
		isInaccessible,
		pendingAction,
		retryResolution,
	} = useSmartLinkAIActions({ api, disabled, getActionHandler, nodeRange, smartlinkUrl, view });
	const metadata = getSmartLinkRovoMetadata({ details, smartlinkUrl });
	const isSmartlinkToolbarV2ExpEnabled = isExperimentEnabled(
		'platform_editor_ai_smartlink_toolbar_v2_exp',
	);
	const { rovoDescriptor, summarizeDescriptor } = getSmartLinkButtonDescriptors({
		api,
		aiGlobalOptIn,
		disabled: disabled || isInaccessible || Boolean(pendingAction),
		intl,
		nodeType,
		nodeRange,
		rangeBaseGenerate,
		smartlinkUrl,
		triggeredForMark,
		metadata,
	});
	const dropdownItems = useMemo(
		() =>
			isSmartlinkToolbarV2ExpEnabled && details
				? getSmartLinkDropdownItems({
						aiGlobalOptIn,
						details,
						intl,
						nodeType,
						smartlinkUrl,
						testIdPrefix: floatingToolbarAIButtonTestID,
						triggeredForMark,
						view,
					})
				: [],
		[
			aiGlobalOptIn,
			details,
			intl,
			isSmartlinkToolbarV2ExpEnabled,
			nodeType,
			smartlinkUrl,
			triggeredForMark,
			view,
		],
	);
	const dropdownItemHandlers = useMemo(
		() =>
			new Map(
				dropdownItems.map((item) => [
					item.testId,
					() => {
						void dispatchAction('dropdown', item.testId);
					},
				]),
			),
		[dispatchAction, dropdownItems],
	);
	const onRovoClick = useCallback(() => {
		void dispatchAction('rovo');
	}, [dispatchAction]);
	const onSummarizeClick = useCallback(() => {
		void dispatchAction('summarize');
	}, [dispatchAction]);
	const actionsDisabled = disabled || isInaccessible || Boolean(pendingAction);
	const dropdownLabel =
		failed && !isInaccessible
			? smartLinkMessages.retry
			: details || isInaccessible
				? smartLinkMessages.noActions
				: smartLinkMessages.loading;

	if (hidden) {
		return null;
	}

	return (
		<SmartLinkToolbarControls
			areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
			disabled={actionsDisabled}
			dropdownItems={dropdownItems}
			dropdownItemHandlers={dropdownItemHandlers}
			dropdownEmptyStateDisabled={
				isSmartlinkToolbarV2ExpEnabled ? !failed || actionsDisabled : undefined
			}
			dropdownEmptyStateLabel={
				isSmartlinkToolbarV2ExpEnabled ? intl.formatMessage(dropdownLabel) : undefined
			}
			forceDropdown={isSmartlinkToolbarV2ExpEnabled}
			intl={intl}
			errorTooltipContent={
				failed && !isInaccessible
					? intl.formatMessage(aiMessages.smartlinkResolutionErrorDescription)
					: undefined
			}
			onRovoClick={onRovoClick}
			onRetry={failed && !isInaccessible ? retryResolution : undefined}
			onSummarizeClick={onSummarizeClick}
			pendingAction={pendingAction}
			rovoDescriptor={rovoDescriptor}
			summarizeDescriptor={summarizeDescriptor}
			view={view}
		/>
	);
};

const renderSmartLinkAIButtonsRenderer = (
	Component: React.ComponentType<SmartLinkAIButtonsRendererProps>,
	props: SmartLinkAIButtonsRendererProps,
) => (
	<Component
		api={props.api}
		aiGlobalOptIn={props.aiGlobalOptIn}
		areAnyNewToolbarFlagsEnabled={props.areAnyNewToolbarFlagsEnabled}
		disabled={props.disabled}
		intl={props.intl}
		isHyperlink={props.isHyperlink}
		nodeRange={props.nodeRange}
		nodeType={props.nodeType}
		rangeBaseGenerate={props.rangeBaseGenerate}
		smartlinkUrl={props.smartlinkUrl}
		view={props.view}
	/>
);

const LegacySmartLinkAIButtonsRenderer = (props: SmartLinkAIButtonsRendererProps) =>
	renderSmartLinkAIButtonsRenderer(
		isExperimentEnabled('platform_editor_fix_link_toolbar_shift')
			? StableSmartLinkAIButtonsRenderer
			: LegacySmartLinkAIButtonsRendererBaseline,
		props,
	);

const RefactoredSmartLinkAIButtonsRenderer = (props: SmartLinkAIButtonsRendererProps) =>
	renderSmartLinkAIButtonsRenderer(
		isExperimentEnabled('platform_editor_fix_link_toolbar_shift')
			? StableSmartLinkAIButtonsRenderer
			: RefactoredSmartLinkAIButtonsRendererBaseline,
		props,
	);

const SmartLinkAIButtonsRenderer = ({
	api,
	aiGlobalOptIn,
	areAnyNewToolbarFlagsEnabled,
	disabled,
	intl,
	isHyperlink,
	nodeType,
	nodeRange,
	rangeBaseGenerate,
	smartlinkUrl,
	view,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	areAnyNewToolbarFlagsEnabled: boolean;
	disabled: boolean;
	intl: IntlShape;
	isHyperlink?: boolean;
	nodeRange?: { from: number; to: number };
	nodeType: NodeType;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	smartlinkUrl: string;
	view: EditorView;
}): React.JSX.Element | null => {
	const Component = isPasteActionsMenuV2Enabled()
		? RefactoredSmartLinkAIButtonsRenderer
		: LegacySmartLinkAIButtonsRenderer;

	return (
		<Component
			api={api}
			aiGlobalOptIn={aiGlobalOptIn}
			areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
			disabled={disabled}
			intl={intl}
			isHyperlink={isHyperlink}
			nodeType={nodeType}
			nodeRange={nodeRange}
			rangeBaseGenerate={rangeBaseGenerate}
			smartlinkUrl={smartlinkUrl}
			view={view}
		/>
	);
};

/**
 * Returns the Smart Link toolbar item, preserving the selected renderer path
 * while allowing the experiment treatment to resolve metadata on activation.
 *
 * The `type: 'custom'` item's `render` function runs inside React (called by
 * the Toolbar component in editor-plugin-floating-toolbar), so it is safe to
 * use React hooks inside SmartLinkAIButtonsRenderer. The resolved-state gate
 * therefore happens at React render time rather than at toolbar-config-build
 * time (which runs outside React in the FloatingToolbarHandler).
 */
export const getSmartLinkAIButtons = ({
	api,
	aiGlobalOptIn,
	areAnyNewToolbarFlagsEnabled,
	disabled,
	intl,
	isHyperlink,
	nodeType,
	nodeRange,
	rangeBaseGenerate,
	smartlinkUrl,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	areAnyNewToolbarFlagsEnabled: boolean;
	disabled: boolean;
	intl: IntlShape;
	isHyperlink?: boolean;
	/**
	 * Document range of the smartlink (card node or hyperlink mark) the toolbar
	 * is anchored to. When omitted, downstream actions fall back to the current
	 * `state.selection`, which is fragile (the toolbar can be shown via hover or
	 * focus without the selection covering the node).
	 */
	nodeRange?: { from: number; to: number };
	nodeType: NodeType;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	smartlinkUrl: string;
}): FloatingToolbarCustom<Command> => ({
	type: 'custom',
	fallback: [],
	render: (view) => {
		if (!view) {
			return null;
		}
		return (
			<SmartLinkAIButtonsRenderer
				key={
					isExperimentEnabled('platform_editor_fix_link_toolbar_shift')
						? `${smartlinkUrl}:${nodeRange?.from}:${nodeRange?.to}`
						: undefined
				}
				view={view}
				intl={intl}
				api={api}
				aiGlobalOptIn={aiGlobalOptIn}
				isHyperlink={isHyperlink}
				smartlinkUrl={smartlinkUrl}
				nodeType={nodeType}
				nodeRange={nodeRange}
				rangeBaseGenerate={rangeBaseGenerate}
				areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
				disabled={disabled}
			/>
		);
	},
});
